/* global React, window, Field, Input, Select, Switch, Modal, Button, t */
// ============================================================
// PLUMME 3 — Prosjektvelger for kostnader
//
// Ett sted for «hvilket prosjekt hoerer denne kostnaden til», brukt fra
// kjopsskjemaet, innboksen, kameraet og ansattportalen. Feltene som lagres er
// definert i src/services/projectCostLink.js.
//
// Velgeren viser seg IKKE for foretak uten aktive prosjekter. Den som ikke
// bruker prosjekter skal ikke se et valg hen aldri kan svare paa.
// ============================================================
(function (global) {
  'use strict';

  const { useState, useEffect } = global.React;

  const activeProjectsFromState = (state) => (Array.isArray(state?.projects) ? state.projects : [])
    .filter((project) => project && project.id && project.status !== 'closed')
    .map((project) => ({ id: project.id, name: project.name || project.projectNumber || project.id, customerName: project.customerName || '' }));

  // Prosjektlista ligger i app-state og oppdateres nar foretaket bytter eller
  // et prosjekt lagres. Begge deler kommer som backend-hendelser.
  function useProjectCostProjects() {
    const backend = global.PlummeBackend?.useBackend ? global.PlummeBackend.useBackend() : null;
    const [projects, setProjects] = useState(() => activeProjectsFromState(global.PlummeBackend?.appState));
    useEffect(() => {
      setProjects(activeProjectsFromState(backend?.appState || global.PlummeBackend?.appState));
    }, [backend?.appState, backend?.activeBusinessId]);
    return projects;
  }

  function ProjectCostLabel({ projects, projectId }) {
    const project = projects.find((entry) => entry.id === projectId);
    if (!project) return null;
    return <span>{project.name}{project.customerName ? <span className="muted"> · {project.customerName}</span> : null}</span>;
  }

  // `value` er projectId, `billable` er om kostnaden skal viderefaktureres.
  // Uten prosjekt gir fakturerbar-bryteren ingen mening og skjules.
  // `projects` kan sendes inn av kalleren naar lista alt er snevret inn — ellers
  // leses den fra app-state som foer.
  function ProjectCostField({ value, billable = true, onChange, disabled, label, hint, projects: projectsProp }) {
    const ownProjects = useProjectCostProjects();
    const projects = projectsProp || ownProjects;
    if (!projects.length) return null;
    return <div className="col gap-8">
      <Field label={label || t('project.cost.field', 'Prosjekt')} hint={hint}>
        <Select disabled={disabled} value={value || ''} onChange={(event) => onChange && onChange({ projectId: event.target.value, projectBillable: event.target.value ? billable !== false : undefined })}>
          <option value="">{t('project.cost.none', 'Ikke pa prosjekt')}</option>
          {projects.map((project) => <option key={project.id} value={project.id}>{project.customerName ? `${project.name} · ${project.customerName}` : project.name}</option>)}
        </Select>
      </Field>
      {value ? <label className="row gap-10" style={{ fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.65 : 1 }}>
        <Switch disabled={disabled} on={billable !== false} onChange={(next) => onChange && onChange({ projectId: value, projectBillable: next })} />
        <span className="t-sm">{t('project.cost.billable', 'Viderefaktureres kunden')}</span>
      </label> : null}
    </div>;
  }

  const LAST_PROJECT_KEY = 'plumme-last-cost-project';
  const lastProjectId = () => { try { return localStorage.getItem(LAST_PROJECT_KEY) || ''; } catch (error) { return ''; } };
  const rememberProjectId = (id) => { try { if (id) localStorage.setItem(LAST_PROJECT_KEY, id); } catch (error) { /* privat modus */ } };

  const projectMatchesQuery = (project, needle) => !needle
    || `${project.name || ''} ${project.customerName || ''}`.toLocaleLowerCase('nb').includes(needle);

  // Steg 1 i bilagsflyten: hvilket prosjekt. Sist brukte prosjekt er forhandsvalgt,
  // fordi den som forer bilag paa prosjekt som regel forer flere paa rad.
  function ProjectCostDialog({ open = true, title, onClose, onPick }) {
    const projects = useProjectCostProjects();
    const [query, setQuery] = useState('');
    const [projectId, setProjectId] = useState(() => {
      const remembered = lastProjectId();
      return remembered || '';
    });
    const [billable, setBillable] = useState(true);
    useEffect(() => {
      if (projectId && projects.some((project) => project.id === projectId)) return;
      setProjectId(projects.length === 1 ? projects[0].id : '');
    }, [projects.length]);
    const confirm = () => {
      if (!projectId) return;
      rememberProjectId(projectId);
      onPick && onPick({ projectId, projectBillable: billable });
    };
    // Nedtrekket alene holder saa lenge lista er kort. Har foretaket mange
    // prosjekter, er soekefeltet raskere enn a rulle gjennom dem paa mobil.
    const needle = query.trim().toLocaleLowerCase('nb');
    const matched = projects.filter((project) => projectMatchesQuery(project, needle));
    // Det valgte prosjektet blir staaende i lista selv om soeket ikke treffer
    // det — ellers stod nedtrekket tomt mens valget fortsatt var gjort.
    const selected = projectId ? projects.find((project) => project.id === projectId) : null;
    const options = selected && !matched.some((project) => project.id === projectId) ? [selected, ...matched] : matched;
    const showSearch = projects.length > 6;
    // portal + top: kameraknappen paa mobil ligger i en `transform`-et wrapper,
    // og en `position:fixed` dialog inni den maales mot knappen i stedet for
    // skjermen — da ble velgeren en usynlig strime nede ved knappen. Portalen
    // loefter dialogen ut til <body>, og `top` forankrer den oeverst slik at
    // nedtrekket har hele skjermhoeyden under seg.
    return <Modal open={open} portal top onClose={onClose} max={460}
      title={title || t('project.cost.pickTitle', 'Velg prosjekt')}
      footer={<div className="row gap-10" style={{ justifyContent: 'flex-end' }}>
        <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
        <Button disabled={!projectId} onClick={confirm}>{t('common.continue', 'Fortsett')}</Button>
      </div>}>
      {showSearch ? <div style={{ marginBottom: 12 }}>
        <Input icon="search" value={query} onChange={(event) => setQuery(event.target.value)}
          placeholder={t('project.cost.search', 'Sok etter prosjekt eller kunde')} />
      </div> : null}
      <ProjectCostField value={projectId} billable={billable} projects={options}
        onChange={(next) => { setProjectId(next.projectId || ''); setBillable(next.projectBillable !== false); }} />
      {showSearch && !options.length ? <div className="t-sm muted" style={{ fontWeight: 700 }}>{t('project.cost.searchEmpty', 'Ingen prosjekter passer soket.')}</div> : null}
    </Modal>;
  }

  const PROJECT_COST_PAGES = { ProjectCostField, ProjectCostDialog, ProjectCostLabel, useProjectCostProjects, activeProjectsFromState };
  Object.assign(global, PROJECT_COST_PAGES);
  global.dispatchEvent?.(new CustomEvent('plumme-page-registered', { detail: { pages: Object.keys(PROJECT_COST_PAGES) } }));
}(window));
